/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import {getMode, getSetting, navigate, ResponseData, showDialog} from '@dvrd/dvr-controls';
import {getBolOAuthClient} from './requestSender.ts';
import {createErrorMessage} from './utils.ts';
import i18n from '../i18n';
import {saveToLocal} from './storageUtil.ts';
import {OAUTH_TYPE_KEY} from './constants.ts';

/**
 * Initiates the Bol.com OAuth authorization flow by redirecting the browser to
 * Bol's login page. After the user authenticates, Bol will redirect back to
 * `https://app.bluemate.nl/oauth/bol` with an authorization code.
 *
 * @param fakeFlow - When `true` and the app is not running in production, skips
 * the real Bol login and navigates straight to the local `/oauth/bol` route.
 * Useful for development and testing without going through Bol's servers.
 * @param oauthState - Optional value forwarded as the OAuth `state` parameter.
 * Bol echoes this back to the redirect URI unchanged, so it can be used to pass
 * directives to the app when the user returns from the OAuth flow — for example,
 * a path to navigate to or a flag indicating which action triggered the connect.
 * The value should be an unrecognizable string (like a UUID) because Bol expects
 * it to be a XSRF token.
 * If omitted, falls back to the `oauthState` setting.
 */
export function onConnectBolOauth(fakeFlow: boolean = false, oauthState?: string) {
    getBolOAuthClient({
        callback: (data: ResponseData) => {
            if (!data.success)
                return showDialog(
                    createErrorMessage(
                        data.message ?? i18n.t('oauth.connectionFailed', 'Het koppelen met Bol is niet gelukt.')
                    )
                );
            const {client_id, redirect_uri, oauth_type} = data;
            // Store in localstorage, we need to know the used OAuth type when the user returns.
            saveToLocal(OAUTH_TYPE_KEY, oauth_type);
            if (getMode() !== 'prod' && fakeFlow) {
                // Extract only the pathname for internal redirect
                const redirect = new URL(redirect_uri).pathname;
                return navigate(redirect);
            }
            if (!oauthState) oauthState = getSetting('oauthState');
            const redirectUri = encodeURIComponent(redirect_uri);
            let uri = `https://login.bol.com/authorize?response_type=code&client_id=${client_id}&redirect_uri=${redirectUri}&prompt=login`;
            if (oauthState) uri += `&state=${oauthState}`;
            window.location.assign(uri);
        }
    });
}
